import { Injectable, inject } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, catchError, filter, map, switchMap, take } from 'rxjs';

import { SessionSelectors } from '../../session/session.selectors';
import { SegmentActions } from '../segment/segment.actions';

import { FeedNotification, NotificationCategoryKey, NotificationTraits } from './notification-settings.model';
import { NotificationsSettingsService } from './notifications-settings.service';

/**
 * Pure function: checks if any channel is enabled for a given category.
 */
function isCategoryEnabled(notifications: FeedNotification[], categoryKey: NotificationCategoryKey): boolean {
  const n = notifications.find(n => n._id === categoryKey);
  return !!n && Object.values(n.channels).some(Boolean);
}

/**
 * Pure function to build notification traits from feed notifications.
 * Exported for testing.
 */
export function buildNotificationTraits(feedNotifications: FeedNotification[]): NotificationTraits {
  return {
    email_enabled: feedNotifications.some(n => n.channels.email),
    web_enabled: feedNotifications.some(n => n.channels.web),
    mobile_enabled: feedNotifications.some(n => n.channels.mobile),
    assignment_enabled: isCategoryEnabled(feedNotifications, NotificationCategoryKey.AssignedToYou),
    comment_enabled: isCategoryEnabled(feedNotifications, NotificationCategoryKey.Comments),
    mention_enabled: isCategoryEnabled(feedNotifications, NotificationCategoryKey.Mentions),
    headlines_enabled: isCategoryEnabled(feedNotifications, NotificationCategoryKey.HeadlinesCascadingMessages),
  };
}

/**
 * Identifies notification settings traits to Segment on app load.
 *
 * Registered at the app level (AppModule) so it fires on startup,
 * even though the implementation lives in the platform state layer
 * alongside the notification settings models and service it depends on.
 */
@Injectable()
export class NotificationSettingsIdentifyEffects {
  private readonly store = inject(Store);
  private readonly notificationsSettingsService = inject(NotificationsSettingsService);

  /**
   * Identify notification settings traits to Segment, once per launch.
   *
   * The condition is read off the store rather than off an action, because the request needs an
   * Access Token and no action says whether one exists. `isAuthenticated` is that token's own
   * precondition — the slice holds Resource Tokens only on `authorized` — so reading it makes
   * `take(1)` mean "the first moment the session can authorize a request", which is the condition
   * this always wanted.
   *
   * Once per launch: `take(1)` completes the stream, so an ordinary refresh does not re-report
   * traits that have not changed.
   */
  identifyNotificationSettings$ = createEffect(() =>
    this.store.select(SessionSelectors.isAuthenticated).pipe(
      filter(isAuthenticated => isAuthenticated),
      take(1),
      switchMap(() =>
        this.notificationsSettingsService.getNotificationSettings().pipe(
          map(feedNotifications => {
            const notificationTraits = buildNotificationTraits(feedNotifications);
            return SegmentActions.identify({
              traits: { notifications: notificationTraits },
            });
          }),
          catchError(() => EMPTY)
        )
      )
    )
  );
}
